// src/lib/weatherVoiceContent.js
//
// Weather Voice (#406) Phase 2 — comment library assembly, language
// lookup, and (test-only-invoked) content validation. Combines each
// language's `{id, text}` list (src/i18n/weatherVoice/is.js, en.js) with a
// single shared metadata registry keyed by `id` — condition/mood/severity
// range/cooldown/CTA are authored exactly once per ID here, never
// duplicated per language, so the "equal metadata across languages for a
// shared ID" requirement holds by construction for real content. The
// validator below still compares metadata across languages generically
// (not merely assuming the shared-registry shape), so the rule itself is
// genuinely enforced and testable with synthetic fixtures.
//
// This module never imports/spreads into src/i18n/translations.js and is
// never resolved through useT() — see is.js's header comment for why.
// evaluateWeatherVoice() (src/lib/weatherVoiceEngine.js) is untouched by
// this ticket; nothing here reconstructs its thresholds or classification.

import { is as isEntries } from "../i18n/weatherVoice/is";
import { en as enEntries } from "../i18n/weatherVoice/en";
import { voiceLevelForCondition } from "./weatherVoiceRules";

// Mirrors the exact string vocabulary declared in weatherVoiceTypes.js's
// WeatherVoiceCondition/TjaldurMood JSDoc typedefs. Phase 1 deliberately
// has no runtime export of these strings (JSDoc typedefs only), so this
// content layer necessarily keeps its own copy of the VOCABULARY (which
// strings are valid at all) — this is not "another condition/mood mapping
// into the engine": it never claims which condition maps to which mood,
// only that a given string is drawn from the known sets. The actual
// condition->mood PAIRING is verified elsewhere, from real engine output
// (see weatherVoiceContent.test.js), and injected into validateWeatherVoiceLibrary
// as `canonicalPairs` rather than hardcoded here.
export const WEATHER_VOICE_KNOWN_CONDITIONS = Object.freeze(
  new Set(["extreme_wind", "heavy_rain", "strong_wind", "cold_wet", "cold", "rain", "sun_wind", "excellent", "good"])
);

export const WEATHER_VOICE_KNOWN_MOODS = Object.freeze(
  new Set([
    "happy",
    "excellent",
    "neutral",
    "suspicious",
    "nervous",
    "struggling",
    "sad",
    "freezing",
    "unimpressed",
    "wrecked",
    "amazed",
    "sleeping",
  ])
);

export const WEATHER_VOICE_CTA_TYPES = Object.freeze(
  new Set(["better_location", "calmer_location", "drier_location", "warmer_location", "best_locations"])
);

const DEFAULT_SEVERITY_MIN = 0;
const DEFAULT_SEVERITY_MAX = 3;
const DEFAULT_COOLDOWN_DAYS = 7;
const DEFAULT_CTA_TYPE = null;

// Shared metadata registry — the single source of truth for every active
// joke entry's condition/mood/voice level/severity-range/cooldown/CTA. Only
// sarcastic (joke) conditions are listed here (#432). Each entry states its
// voice level explicitly; a missing or policy-mismatched level is never
// defaulted to sarcastic.
const WEATHER_VOICE_COMMENT_METADATA = Object.freeze({
  good_01: { condition: "good", mood: "happy", voiceLevel: "sarcastic" },
  good_02: { condition: "good", mood: "happy", voiceLevel: "sarcastic" },
  good_03: { condition: "good", mood: "happy", voiceLevel: "sarcastic" },
  good_04: { condition: "good", mood: "happy", voiceLevel: "sarcastic" },
  good_05: { condition: "good", mood: "happy", voiceLevel: "sarcastic" },
  good_06: { condition: "good", mood: "happy", voiceLevel: "sarcastic" },
  good_07: { condition: "good", mood: "happy", voiceLevel: "sarcastic" },
  good_08: { condition: "good", mood: "happy", voiceLevel: "sarcastic" },
  good_09: { condition: "good", mood: "happy", voiceLevel: "sarcastic" },
  good_10: { condition: "good", mood: "happy", voiceLevel: "sarcastic" },
  good_11: { condition: "good", mood: "happy", voiceLevel: "sarcastic" },
  good_12: { condition: "good", mood: "happy", voiceLevel: "sarcastic" },
  good_13: { condition: "good", mood: "happy", voiceLevel: "sarcastic" },
  good_14: { condition: "good", mood: "happy", voiceLevel: "sarcastic" },
  good_15: { condition: "good", mood: "happy", voiceLevel: "sarcastic" },
  good_16: { condition: "good", mood: "happy", voiceLevel: "sarcastic" },
  good_17: { condition: "good", mood: "happy", voiceLevel: "sarcastic" },
  good_18: { condition: "good", mood: "happy", voiceLevel: "sarcastic" },
  good_19: { condition: "good", mood: "happy", voiceLevel: "sarcastic" },
  good_20: { condition: "good", mood: "happy", voiceLevel: "sarcastic" },
  good_21: { condition: "good", mood: "happy", voiceLevel: "sarcastic" },
  good_22: { condition: "good", mood: "happy", voiceLevel: "sarcastic" },
  good_23: { condition: "good", mood: "happy", voiceLevel: "sarcastic" },
  excellent_01: { condition: "excellent", mood: "excellent", voiceLevel: "sarcastic" },
  excellent_03: { condition: "excellent", mood: "excellent", voiceLevel: "sarcastic" },
  excellent_04: { condition: "excellent", mood: "excellent", voiceLevel: "sarcastic" },
  excellent_05: { condition: "excellent", mood: "excellent", voiceLevel: "sarcastic" },
  excellent_06: { condition: "excellent", mood: "excellent", voiceLevel: "sarcastic" },
  excellent_07: { condition: "excellent", mood: "excellent", voiceLevel: "sarcastic" },
  excellent_08: { condition: "excellent", mood: "excellent", voiceLevel: "sarcastic" },
  excellent_09: { condition: "excellent", mood: "excellent", voiceLevel: "sarcastic" },
  excellent_10: { condition: "excellent", mood: "excellent", voiceLevel: "sarcastic" },
  excellent_11: { condition: "excellent", mood: "excellent", voiceLevel: "sarcastic" },
  excellent_12: { condition: "excellent", mood: "excellent", voiceLevel: "sarcastic" },
  excellent_13: { condition: "excellent", mood: "excellent", voiceLevel: "sarcastic" },
  excellent_14: { condition: "excellent", mood: "excellent", voiceLevel: "sarcastic" },
  excellent_15: { condition: "excellent", mood: "excellent", voiceLevel: "sarcastic" },
  excellent_16: { condition: "excellent", mood: "excellent", voiceLevel: "sarcastic" },
  excellent_17: { condition: "excellent", mood: "excellent", voiceLevel: "sarcastic" },
  excellent_18: { condition: "excellent", mood: "excellent", voiceLevel: "sarcastic" },
  excellent_19: { condition: "excellent", mood: "excellent", voiceLevel: "sarcastic" },
  excellent_20: { condition: "excellent", mood: "excellent", voiceLevel: "sarcastic" },
  excellent_21: { condition: "excellent", mood: "excellent", voiceLevel: "sarcastic" },
  excellent_22: { condition: "excellent", mood: "excellent", voiceLevel: "sarcastic" },
  excellent_23: { condition: "excellent", mood: "excellent", voiceLevel: "sarcastic" },
  rain_01: { condition: "rain", mood: "unimpressed", voiceLevel: "sarcastic" },
  rain_02: { condition: "rain", mood: "unimpressed", voiceLevel: "sarcastic" },
  rain_03: { condition: "rain", mood: "unimpressed", voiceLevel: "sarcastic" },
  rain_04: { condition: "rain", mood: "unimpressed", voiceLevel: "sarcastic" },
  rain_05: { condition: "rain", mood: "unimpressed", voiceLevel: "sarcastic" },
  rain_06: { condition: "rain", mood: "unimpressed", voiceLevel: "sarcastic" },
  rain_07: { condition: "rain", mood: "unimpressed", voiceLevel: "sarcastic" },
  rain_08: { condition: "rain", mood: "unimpressed", voiceLevel: "sarcastic" },
  rain_09: { condition: "rain", mood: "unimpressed", voiceLevel: "sarcastic" },
  rain_10: { condition: "rain", mood: "unimpressed", voiceLevel: "sarcastic" },
  rain_11: { condition: "rain", mood: "unimpressed", voiceLevel: "sarcastic" },
  rain_12: { condition: "rain", mood: "unimpressed", voiceLevel: "sarcastic" },
  rain_13: { condition: "rain", mood: "unimpressed", voiceLevel: "sarcastic" },
  rain_14: { condition: "rain", mood: "unimpressed", voiceLevel: "sarcastic" },
  rain_15: { condition: "rain", mood: "unimpressed", voiceLevel: "sarcastic" },
  rain_16: { condition: "rain", mood: "unimpressed", voiceLevel: "sarcastic" },
  rain_17: { condition: "rain", mood: "unimpressed", voiceLevel: "sarcastic" },
  rain_18: { condition: "rain", mood: "unimpressed", voiceLevel: "sarcastic" },
  rain_19: { condition: "rain", mood: "unimpressed", voiceLevel: "sarcastic" },
  rain_20: { condition: "rain", mood: "unimpressed", voiceLevel: "sarcastic" },
  rain_21: { condition: "rain", mood: "unimpressed", voiceLevel: "sarcastic" },
  rain_22: { condition: "rain", mood: "unimpressed", voiceLevel: "sarcastic" },
  rain_23: { condition: "rain", mood: "unimpressed", voiceLevel: "sarcastic" },
  cold_01: { condition: "cold", mood: "freezing", voiceLevel: "sarcastic" },
  cold_03: { condition: "cold", mood: "freezing", voiceLevel: "sarcastic" },
  cold_04: { condition: "cold", mood: "freezing", voiceLevel: "sarcastic" },
  cold_05: { condition: "cold", mood: "freezing", voiceLevel: "sarcastic" },
  cold_06: { condition: "cold", mood: "freezing", voiceLevel: "sarcastic" },
  cold_07: { condition: "cold", mood: "freezing", voiceLevel: "sarcastic" },
  cold_08: { condition: "cold", mood: "freezing", voiceLevel: "sarcastic" },
  cold_09: { condition: "cold", mood: "freezing", voiceLevel: "sarcastic" },
  cold_10: { condition: "cold", mood: "freezing", voiceLevel: "sarcastic" },
  cold_11: { condition: "cold", mood: "freezing", voiceLevel: "sarcastic" },
  cold_12: { condition: "cold", mood: "freezing", voiceLevel: "sarcastic" },
  cold_13: { condition: "cold", mood: "freezing", voiceLevel: "sarcastic" },
  cold_14: { condition: "cold", mood: "freezing", voiceLevel: "sarcastic" },
  cold_15: { condition: "cold", mood: "freezing", voiceLevel: "sarcastic" },
  cold_16: { condition: "cold", mood: "freezing", voiceLevel: "sarcastic" },
  cold_17: { condition: "cold", mood: "freezing", voiceLevel: "sarcastic" },
  cold_18: { condition: "cold", mood: "freezing", voiceLevel: "sarcastic" },
  cold_19: { condition: "cold", mood: "freezing", voiceLevel: "sarcastic" },
  cold_20: { condition: "cold", mood: "freezing", voiceLevel: "sarcastic" },
  cold_21: { condition: "cold", mood: "freezing", voiceLevel: "sarcastic" },
  cold_22: { condition: "cold", mood: "freezing", voiceLevel: "sarcastic" },
  cold_23: { condition: "cold", mood: "freezing", voiceLevel: "sarcastic" },
  cold_24: { condition: "cold", mood: "freezing", voiceLevel: "sarcastic" },
  sun_wind_01: { condition: "sun_wind", mood: "suspicious", voiceLevel: "sarcastic" },
  sun_wind_02: { condition: "sun_wind", mood: "suspicious", voiceLevel: "sarcastic" },
  sun_wind_03: { condition: "sun_wind", mood: "suspicious", voiceLevel: "sarcastic" },
  sun_wind_04: { condition: "sun_wind", mood: "suspicious", voiceLevel: "sarcastic" },
  sun_wind_05: { condition: "sun_wind", mood: "suspicious", voiceLevel: "sarcastic" },
  sun_wind_06: { condition: "sun_wind", mood: "suspicious", voiceLevel: "sarcastic" },
  sun_wind_07: { condition: "sun_wind", mood: "suspicious", voiceLevel: "sarcastic" },
  sun_wind_08: { condition: "sun_wind", mood: "suspicious", voiceLevel: "sarcastic" },
  sun_wind_09: { condition: "sun_wind", mood: "suspicious", voiceLevel: "sarcastic" },
  sun_wind_10: { condition: "sun_wind", mood: "suspicious", voiceLevel: "sarcastic" },
  sun_wind_11: { condition: "sun_wind", mood: "suspicious", voiceLevel: "sarcastic" },
  sun_wind_12: { condition: "sun_wind", mood: "suspicious", voiceLevel: "sarcastic" },
  sun_wind_13: { condition: "sun_wind", mood: "suspicious", voiceLevel: "sarcastic" },
  sun_wind_14: { condition: "sun_wind", mood: "suspicious", voiceLevel: "sarcastic" },
  sun_wind_15: { condition: "sun_wind", mood: "suspicious", voiceLevel: "sarcastic" },
  sun_wind_16: { condition: "sun_wind", mood: "suspicious", voiceLevel: "sarcastic" },
  sun_wind_17: { condition: "sun_wind", mood: "suspicious", voiceLevel: "sarcastic" },
  sun_wind_18: { condition: "sun_wind", mood: "suspicious", voiceLevel: "sarcastic" },
  sun_wind_19: { condition: "sun_wind", mood: "suspicious", voiceLevel: "sarcastic" },
  sun_wind_20: { condition: "sun_wind", mood: "suspicious", voiceLevel: "sarcastic" },
  sun_wind_21: { condition: "sun_wind", mood: "suspicious", voiceLevel: "sarcastic" },
  sun_wind_22: { condition: "sun_wind", mood: "suspicious", voiceLevel: "sarcastic" },
});

// Retired joke IDs, permanently reserved: never reused, repurposed, or active.
// wind_extreme_*, wind_strong_01–03, rain_heavy_01–03 and cold_wet_01–03 were
// retired by #432 (cautious/serious conditions). cold_02 and excellent_02 were
// retired by #420 (sarcastic conditions: too close to a retained line, and an
// imperative/direct address). Their released public share pages stay on disk,
// unchanged. Never-released reserves and excluded proposals are not listed here.
export const RETIRED_JOKE_IDS = Object.freeze(
  new Set([
    "wind_extreme_01",
    "wind_extreme_02",
    "wind_extreme_03",
    "wind_extreme_04",
    "wind_extreme_05",
    "wind_strong_01",
    "wind_strong_02",
    "wind_strong_03",
    "rain_heavy_01",
    "rain_heavy_02",
    "rain_heavy_03",
    "cold_wet_01",
    "cold_wet_02",
    "cold_wet_03",
    "cold_02",
    "excellent_02",
  ])
);

export const WEATHER_VOICE_KNOWN_IDS = Object.freeze(new Set(Object.keys(WEATHER_VOICE_COMMENT_METADATA)));

function normalizeMetadata(meta) {
  return {
    condition: meta.condition,
    mood: meta.mood,
    voiceLevel: meta.voiceLevel,
    severityMin: meta.severityMin ?? DEFAULT_SEVERITY_MIN,
    severityMax: meta.severityMax ?? DEFAULT_SEVERITY_MAX,
    repeatCooldownDays: meta.repeatCooldownDays ?? DEFAULT_COOLDOWN_DAYS,
    ctaType: meta.ctaType ?? DEFAULT_CTA_TYPE,
  };
}

function isToneConsistent(meta) {
  return meta.voiceLevel === "sarcastic" && voiceLevelForCondition(meta.condition) === "sarcastic";
}

const LANGUAGE_TEXT_ENTRIES = { is: isEntries, en: enEntries };

function isNonEmptyText(text) {
  return typeof text === "string" && text.trim().length > 0;
}

// ── Dev-only diagnostics (Ticket 412, #412) ────────────────────────────
// Bounded: each distinct case warns at most once per page session via a
// module-level dedup set (mirrors this codebase's existing
// clearAuroraDecisionCache pattern — see auroraDecisionCache.js — for a
// module-level cache with a test-only clear hook). Dev-only
// (import.meta.env.DEV, the same convention already used in analytics.js/
// App.jsx); reads only this module's own static content, never user data,
// analytics, or network calls. Deliberately kept OUT of
// weatherVoiceEngine.js/weatherVoiceSelector.js, which stay pure — this is
// the "content/integration boundary" the approved prompt calls for.
const warnedIncompleteLanguages = new Set();
const warnedEmptyEligiblePools = new Set();

export function clearWeatherVoiceDevDiagnosticsForTests() {
  warnedIncompleteLanguages.clear();
  warnedEmptyEligiblePools.clear();
}

// Called from getWeatherVoiceLibrary() itself so any consumer (not only
// useWeatherVoice.js) gets the same warning the first time it resolves a
// genuinely incomplete language — a stale/missing/blank ID would otherwise
// only be discovered by noticing a shorter-than-expected pool.
function devWarnIncompleteLanguage(lang, rawEntries) {
  if (!import.meta.env.DEV) return;
  if (warnedIncompleteLanguages.has(lang)) return;

  const textById = new Map();
  for (const entry of rawEntries) {
    if (typeof entry?.id === "string") textById.set(entry.id, entry.text);
  }

  const missing = [];
  for (const id of WEATHER_VOICE_KNOWN_IDS) {
    if (!isNonEmptyText(textById.get(id))) missing.push(id);
  }
  if (missing.length === 0) return;

  warnedIncompleteLanguages.add(lang);
  console.warn(`[weatherVoiceContent] "${lang}" is missing or has blank translations for: ${missing.join(", ")}`);
}

/**
 * devWarnEmptyEligiblePool(lang, condition, mood) — called from
 * useWeatherVoice.js's selection effect (the integration boundary, which
 * is the only place that has both the engine's result AND the selector's
 * result) when the engine wanted to show something but the resolved
 * language's eligible pool for that exact condition/mood was empty — the
 * approved prompt's "exceptional" wholly-absent-pool case. Fires at most
 * once per (lang, condition, mood) per page session.
 */
export function devWarnEmptyEligiblePool(lang, condition, mood) {
  if (!import.meta.env.DEV) return;
  const key = `${lang}|${condition}|${mood}`;
  if (warnedEmptyEligiblePools.has(key)) return;
  warnedEmptyEligiblePools.add(key);
  console.warn(
    `[weatherVoiceContent] no eligible "${lang}" Weather Voice content for condition="${condition}" mood="${mood}" — failing closed (silent) rather than showing untranslated/fabricated/stale text.`
  );
}

/**
 * getWeatherVoiceLibrary(lang) -> WeatherVoiceCommentEntry[] | null
 *
 * Pure (aside from the bounded, dev-only diagnostic below — see its own
 * doc comment). `lang` must be exactly "is" or "en"; any other value
 * (including missing/unsupported languages) returns `null` — never a
 * silent fallback to Icelandic. Ticket 412 (#412): both "is" and "en" are
 * now genuinely complete (ID-for-ID parity — see
 * `validateWeatherVoiceLanguageCompleteness` and its test coverage; the
 * primary library holds only sarcastic entries since #432);
 * `"en"` is no longer the deliberately-empty MVP placeholder it started as.
 * An entry whose registered metadata is missing OR whose `text` is
 * missing/blank is silently dropped here — never surfaced as a broken
 * card — so the selector automatically falls through to another valid
 * entry for the same condition/mood/severity; this is what makes a
 * partially-broken translation self-heal at the existing selector
 * boundary without any selector change (see docs/ai/tasks/ticket-412).
 *
 * @param {string} lang
 * @returns {import("./weatherVoiceTypes").WeatherVoiceCommentEntry[] | null}
 */
export function getWeatherVoiceLibrary(lang) {
  const textEntries = LANGUAGE_TEXT_ENTRIES[lang];
  if (!Array.isArray(textEntries)) return null;

  devWarnIncompleteLanguage(lang, textEntries);

  const out = [];
  for (const { id, text } of textEntries) {
    const meta = WEATHER_VOICE_COMMENT_METADATA[id];
    if (!meta) continue; // defensive: content without registered metadata is never surfaced
    if (!isToneConsistent(meta)) continue; // #432: a joke whose tone does not match its condition's policy is never surfaced
    if (typeof text !== "string" || text.trim().length === 0) continue; // defensive: blank/invalid translated text is never surfaced — selector picks another eligible entry instead
    out.push({ id, text, ...normalizeMetadata(meta) });
  }
  return out;
}

/**
 * getWeatherVoiceCommentMetadataById(id) -> WeatherVoiceCommentMetadata | null
 *
 * Pure, read-only, language-independent lookup — Revision 2 (#406): added
 * so weatherVoiceHistory.js can validate a shown presentation's
 * condition/mood/severity-range/CTA against the SAME single metadata
 * registry every language entry is built from, rather than duplicating a
 * second condition/mood table or reconstructing Phase 1 thresholds.
 * Returns `null` for any id not in the canonical registry.
 *
 * @param {string} id
 * @returns {import("./weatherVoiceTypes").WeatherVoiceCommentMetadata | null}
 */
export function getWeatherVoiceCommentMetadataById(id) {
  if (typeof id !== "string") return null;
  const meta = WEATHER_VOICE_COMMENT_METADATA[id];
  if (!meta) return null;
  return normalizeMetadata(meta);
}

function isNonEmptyAsciiId(id) {
  return typeof id === "string" && /^[A-Za-z0-9_]+$/.test(id);
}

function pairKey(condition, mood) {
  return `${condition}|${mood}`;
}

/**
 * validateWeatherVoiceLibrary({ languages, canonicalPairs }) -> { valid, errors }
 *
 * Pure, generic validator — test/build-tooling use only (approved prompt:
 * "should run in tests/build tooling, not repeatedly scan all content in
 * production render paths"). `languages` is `{ [lang]: WeatherVoiceCommentEntry[] }`
 * (real output of getWeatherVoiceLibrary(), or a synthetic fixture built
 * the same shape for negative tests). `canonicalPairs` is an optional
 * `Set<"condition|mood">` of pairs actually reachable from real Phase 1
 * engine output — inject it from real evaluateWeatherVoice() fixtures in
 * the caller/test; this module never hardcodes its own copy of that
 * pairing (it would risk drifting from the engine's real behavior).
 *
 * @param {{ languages: Record<string, import("./weatherVoiceTypes").WeatherVoiceCommentEntry[]>, canonicalPairs?: Set<string> }} args
 * @returns {{ valid: boolean, errors: string[] }}
 */
export function validateWeatherVoiceLibrary({ languages, canonicalPairs } = {}) {
  const errors = [];
  const occurrencesById = new Map();

  for (const [lang, entries] of Object.entries(languages || {})) {
    if (!Array.isArray(entries)) {
      errors.push(`${lang}: entries must be an array`);
      continue;
    }

    const seenInLanguage = new Set();

    for (const entry of entries) {
      const id = entry?.id;

      if (!isNonEmptyAsciiId(id)) {
        errors.push(`${lang}: invalid id ${JSON.stringify(id)}`);
        continue;
      }
      if (seenInLanguage.has(id)) {
        errors.push(`${lang}: duplicate id within language: ${id}`);
      }
      seenInLanguage.add(id);

      if (!WEATHER_VOICE_KNOWN_CONDITIONS.has(entry.condition)) {
        errors.push(`${lang}/${id}: unsupported condition ${JSON.stringify(entry.condition)}`);
      }
      if (!WEATHER_VOICE_KNOWN_MOODS.has(entry.mood)) {
        errors.push(`${lang}/${id}: unsupported mood ${JSON.stringify(entry.mood)}`);
      }
      if (RETIRED_JOKE_IDS.has(id)) {
        errors.push(`${lang}/${id}: retired joke id must not be active`);
      }
      if (entry.voiceLevel !== "sarcastic") {
        errors.push(`${lang}/${id}: joke library entries must be sarcastic, got ${JSON.stringify(entry.voiceLevel)}`);
      } else if (WEATHER_VOICE_KNOWN_CONDITIONS.has(entry.condition) && voiceLevelForCondition(entry.condition) !== "sarcastic") {
        errors.push(`${lang}/${id}: condition ${entry.condition} is not sarcastic under the voice-level policy`);
      }
      if (
        canonicalPairs &&
        WEATHER_VOICE_KNOWN_CONDITIONS.has(entry.condition) &&
        WEATHER_VOICE_KNOWN_MOODS.has(entry.mood) &&
        !canonicalPairs.has(pairKey(entry.condition, entry.mood))
      ) {
        errors.push(`${lang}/${id}: condition/mood pair ${entry.condition}/${entry.mood} is not a canonical Phase 1 pairing`);
      }

      if (typeof entry.text !== "string" || entry.text.trim().length === 0) {
        errors.push(`${lang}/${id}: text must be a nonempty, non-whitespace-only string`);
      }

      const { severityMin, severityMax } = entry;
      if (
        !Number.isInteger(severityMin) ||
        !Number.isInteger(severityMax) ||
        severityMin < 0 ||
        severityMax > 3 ||
        severityMin > severityMax
      ) {
        errors.push(`${lang}/${id}: invalid severity range [${severityMin}, ${severityMax}]`);
      }

      if (!Number.isFinite(entry.repeatCooldownDays) || entry.repeatCooldownDays < 0) {
        errors.push(`${lang}/${id}: invalid repeatCooldownDays ${JSON.stringify(entry.repeatCooldownDays)}`);
      }

      if (entry.ctaType !== null && entry.ctaType !== undefined && !WEATHER_VOICE_CTA_TYPES.has(entry.ctaType)) {
        errors.push(`${lang}/${id}: invalid ctaType ${JSON.stringify(entry.ctaType)}`);
      }

      if (!occurrencesById.has(id)) occurrencesById.set(id, []);
      occurrencesById.get(id).push({ lang, entry });
    }
  }

  for (const [id, occurrences] of occurrencesById) {
    if (occurrences.length < 2) continue;
    const [first, ...rest] = occurrences;
    for (const { lang, entry } of rest) {
      const same =
        entry.condition === first.entry.condition &&
        entry.mood === first.entry.mood &&
        entry.voiceLevel === first.entry.voiceLevel &&
        entry.severityMin === first.entry.severityMin &&
        entry.severityMax === first.entry.severityMax &&
        entry.repeatCooldownDays === first.entry.repeatCooldownDays &&
        entry.ctaType === first.entry.ctaType;
      if (!same) {
        errors.push(`${id}: metadata mismatch between languages "${first.lang}" and "${lang}" (text may differ, metadata must not)`);
      }
    }
  }

  return { valid: errors.length === 0, errors };
}

/**
 * validateWeatherVoiceLanguageCompleteness({ languages }) -> { valid, errors }
 *
 * Ticket 412 (#412) — cross-language ID PARITY validator, distinct from
 * validateWeatherVoiceLibrary above: that function checks metadata/text
 * validity for entries that ARE present, but never checks whether an
 * expected id is missing entirely. This one does exactly that: every
 * canonical id in WEATHER_VOICE_KNOWN_IDS must have exactly one non-blank
 * entry in each given language's RAW `{id, text}` array (the is.js/en.js
 * export shape, before metadata is joined in by getWeatherVoiceLibrary).
 * Pure, test/build-tooling use only — never called from a production
 * render path.
 *
 * @param {{ languages: Record<string, Array<{id: string, text: string}>> }} args
 * @returns {{ valid: boolean, errors: string[] }}
 */
export function validateWeatherVoiceLanguageCompleteness({ languages } = {}) {
  const errors = [];

  for (const [lang, entries] of Object.entries(languages || {})) {
    if (!Array.isArray(entries)) {
      errors.push(`${lang}: entries must be an array`);
      continue;
    }

    const seen = new Set();
    for (const entry of entries) {
      const id = entry?.id;
      if (typeof id !== "string") continue; // malformed id shape is validateWeatherVoiceLibrary's concern, not parity

      if (seen.has(id)) {
        errors.push(`${lang}/${id}: duplicate id`);
      }
      seen.add(id);

      if (!isNonEmptyText(entry?.text)) {
        errors.push(`${lang}/${id}: text is missing or blank`);
      }
    }

    for (const id of WEATHER_VOICE_KNOWN_IDS) {
      if (!seen.has(id)) errors.push(`${lang}/${id}: missing`);
    }
  }

  return { valid: errors.length === 0, errors };
}
